import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useAuth } from "@/lib/auth";
import { useTheme } from "@/lib/theme";
import { OSM_TILE_URL, OSM_ATTRIBUTION, tileClassForTheme } from "@/lib/tiles";
import {
    listMyPhotos,
    uploadMyPhoto,
    updateMyPhoto,
    deleteMyPhoto,
    userPhotoImageUrl,
    fetchMyRidden,
    fetchRoutes,
    API,
} from "@/lib/api";
import { toast } from "sonner";
import { Upload, MapPin, Trash2, Globe, Lock, Camera, Flag, LogOut, Share2, Check } from "lucide-react";

const photoIcon = (thumbUrl) =>
    L.divIcon({
        html: `<div style="width:52px;height:52px;border-radius:50%;border:2px solid #E87722;overflow:hidden;box-shadow:0 4px 12px rgba(0,0,0,0.5);"><img src="${thumbUrl}" style="width:100%;height:100%;object-fit:cover" /></div>`,
        className: "photo-marker",
        iconSize: [52, 52],
        iconAnchor: [26, 26],
    });

export default function Passport() {
    const { user, isAuthed, logout } = useAuth();
    const { theme } = useTheme();
    const navigate = useNavigate();
    const [photos, setPhotos] = useState([]);
    const [ridden, setRidden] = useState({});
    const [routes, setRoutes] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [visibility, setVisibility] = useState("private");
    const [caption, setCaption] = useState("");
    const fileRef = useRef(null);

    useEffect(() => {
        document.title = "Rider Passport · MotorcycleRoutesIreland";
    }, []);

    useEffect(() => {
        if (isAuthed === false) navigate("/signin?return=/passport");
    }, [isAuthed, navigate]);

    useEffect(() => {
        if (!isAuthed) return;
        Promise.all([listMyPhotos(), fetchMyRidden(), fetchRoutes()])
            .then(([p, r, routes]) => {
                setPhotos(p.photos || []);
                setRidden(r.ridden || {});
                setRoutes(routes || []);
            })
            .catch(() => {});
    }, [isAuthed]);

    const stats = useMemo(() => {
        const totalPhotos = photos.length;
        let stopsRidden = 0;
        let routesTouched = 0;
        for (const slug of Object.keys(ridden || {})) {
            const list = ridden[slug] || [];
            if (list.length > 0) routesTouched += 1;
            stopsRidden += list.length;
        }
        const routesById = new Map(routes.map((r) => [r.slug, r]));
        return { totalPhotos, stopsRidden, routesTouched, routesById };
    }, [photos, ridden, routes]);

    const geotaggedPhotos = useMemo(
        () => photos.filter((p) => p.lat != null && p.lng != null),
        [photos]
    );

    const mapCenter = useMemo(() => {
        if (geotaggedPhotos.length === 0) return [53.4, -7.9];
        const lat = geotaggedPhotos.reduce((s, p) => s + p.lat, 0) / geotaggedPhotos.length;
        const lng = geotaggedPhotos.reduce((s, p) => s + p.lng, 0) / geotaggedPhotos.length;
        return [lat, lng];
    }, [geotaggedPhotos]);

    const onUpload = async (e) => {
        e?.preventDefault();
        const file = fileRef.current?.files?.[0];
        if (!file) {
            toast.error("Choose a photo first.");
            return;
        }
        setUploading(true);
        try {
            const { photo } = await uploadMyPhoto({ file, visibility, caption });
            setPhotos((prev) => [photo, ...prev]);
            setCaption("");
            if (fileRef.current) fileRef.current.value = "";
            if (photo.lat == null) {
                toast.success("Photo uploaded", { description: "No GPS in EXIF, so it won't appear on the map." });
            } else if (photo.route_slug) {
                toast.success("Photo uploaded", { description: `Matched to ${photo.route_slug}.` });
            } else {
                toast.success("Photo uploaded.");
            }
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Upload failed.");
        } finally {
            setUploading(false);
        }
    };

    const onToggleVisibility = async (photo) => {
        const next = photo.visibility === "public" ? "private" : "public";
        try {
            const { photo: updated } = await updateMyPhoto(photo.photo_id, { visibility: next });
            setPhotos((prev) => prev.map((p) => (p.photo_id === photo.photo_id ? updated : p)));
            toast.success(`Photo is now ${next}.`);
        } catch {
            toast.error("Couldn't update visibility.");
        }
    };

    const onDelete = async (photo) => {
        if (!window.confirm("Delete this photo?")) return;
        try {
            await deleteMyPhoto(photo.photo_id);
            setPhotos((prev) => prev.filter((p) => p.photo_id !== photo.photo_id));
            toast.success("Photo deleted.");
        } catch {
            toast.error("Delete failed.");
        }
    };

    if (isAuthed === null) {
        return (
            <div className="min-h-[70vh] flex items-center justify-center">
                <div className="animate-pulse text-[var(--fg-dim)] uppercase tracking-[0.24em] text-xs font-body">Loading…</div>
            </div>
        );
    }
    if (!isAuthed) return null; // Redirecting…

    return (
        <div data-testid="passport-page" className="min-h-screen bg-[var(--bg)]">
            {/* Hero header */}
            <section className="pt-32 pb-10 border-b border-[var(--border-hair)]">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <div className="flex flex-wrap items-end justify-between gap-6">
                        <div>
                            <div className="eyebrow mb-3">Rider Passport</div>
                            <h1 className="font-display font-black uppercase text-[var(--fg)] text-4xl md:text-6xl leading-none tracking-tight">
                                Welcome back,<br />
                                <span className="text-[#E87722]">{user?.name || user?.email}</span>.
                            </h1>
                        </div>
                        <div className="flex items-center gap-3">
                            {user?.picture && (
                                <img src={user.picture} alt="" className="w-10 h-10 rounded-full border border-[var(--border-hair)]" referrerPolicy="no-referrer" />
                            )}
                            <SharePassportButton userId={user?.user_id} />
                            <button
                                onClick={logout}
                                data-testid="passport-logout-btn"
                                className="inline-flex items-center gap-2 border border-[var(--border-hair)] hover:border-[#E87722] hover:text-[#E87722] text-[var(--fg-muted)] px-4 py-2 font-display font-bold uppercase tracking-[0.2em] text-xs transition-colors"
                            >
                                <LogOut size={14} /> Sign out
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* Stats */}
            <section className="border-b border-[var(--border-hair)]">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-3 divide-x divide-[var(--border-hair)]">
                    {[
                        { label: "Photos", value: stats.totalPhotos, icon: Camera },
                        { label: "Stops ridden", value: stats.stopsRidden, icon: Flag },
                        { label: "Routes touched", value: stats.routesTouched, icon: MapPin },
                    ].map((s) => (
                        <div key={s.label} className="py-8 px-4 md:px-6" data-testid={`passport-stat-${s.label.toLowerCase().replace(/\s/g, "-")}`}>
                            <s.icon size={20} className="text-[#E87722] mb-3" />
                            <div className="font-display font-black text-[var(--fg)] text-4xl md:text-5xl leading-none">{s.value}</div>
                            <div className="mt-2 text-[10px] uppercase tracking-[0.28em] text-[var(--fg-dim)] font-body">{s.label}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Share card preview */}
            <section className="border-b border-[var(--border-hair)]">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 lg:py-16 grid grid-cols-1 md:grid-cols-[1fr_auto] items-center gap-8">
                    <div>
                        <div className="eyebrow mb-3">Your Share Card</div>
                        <h2 className="font-display font-black uppercase text-[var(--fg)] text-2xl md:text-3xl leading-none tracking-tight mb-3">
                            Screenshot &amp; post.
                        </h2>
                        <p className="text-[var(--fg-muted)] font-body text-sm max-w-xl">
                            Every time you tick a stop or share a photo, your passport card updates automatically. Right-click the preview to save it, or tap <span className="text-[#E87722] font-bold">Share Passport</span> above to send the shareable link.
                        </p>
                    </div>
                    {user?.user_id && (
                        <div className="relative border border-[var(--border-hair)] bg-black overflow-hidden" style={{ width: 360, maxWidth: "100%", aspectRatio: "1200/630" }}>
                            <img
                                src={`${API}/passport/${user.user_id}/og.png?refresh=1&t=${photos.length}-${stats.stopsRidden}`}
                                alt="Your passport share card"
                                data-testid="passport-share-preview"
                                className="w-full h-full object-cover"
                            />
                        </div>
                    )}
                </div>
            </section>

            {/* Map */}
            <section className="py-12 lg:py-16 border-b border-[var(--border-hair)]">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
                        <div>
                            <div className="eyebrow mb-2">Your Map</div>
                            <h2 className="font-display font-black uppercase text-[var(--fg)] text-2xl md:text-3xl leading-none tracking-tight">
                                Every ride, one place.
                            </h2>
                        </div>
                        <div className="text-[var(--fg-dim)] text-xs uppercase tracking-[0.24em] font-body">
                            {geotaggedPhotos.length} of {photos.length} photos have GPS
                        </div>
                    </div>
                    <div className="border border-[var(--border-hair)]" style={{ height: 460 }}>
                        <MapContainer center={mapCenter} zoom={geotaggedPhotos.length > 0 ? 7 : 6} style={{ height: "100%", width: "100%" }} scrollWheelZoom={false} className={tileClassForTheme(theme)}>
                            <TileLayer url={OSM_TILE_URL} attribution={OSM_ATTRIBUTION} />
                            {geotaggedPhotos.map((p) => (
                                <Marker key={p.photo_id} position={[p.lat, p.lng]} icon={photoIcon(userPhotoImageUrl(p.photo_id))}>
                                    <Popup>
                                        <div style={{ minWidth: 200 }}>
                                            <img src={userPhotoImageUrl(p.photo_id)} alt="" style={{ width: "100%", borderRadius: 4, marginBottom: 6 }} />
                                            {p.caption && <div style={{ fontSize: 12, color: "#F8FAFC", marginBottom: 4 }}>{p.caption}</div>}
                                            {p.route_slug && (
                                                <Link to={`/routes/${p.route_slug}`} style={{ color: "#E87722", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.16em" }}>
                                                    View {p.route_slug} →
                                                </Link>
                                            )}
                                        </div>
                                    </Popup>
                                </Marker>
                            ))}
                        </MapContainer>
                    </div>
                </div>
            </section>

            {/* Upload */}
            <section className="py-12 lg:py-16 border-b border-[var(--border-hair)]">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <div className="eyebrow mb-3">Upload a photo</div>
                    <h2 className="font-display font-black uppercase text-[var(--fg)] text-2xl md:text-3xl leading-none tracking-tight mb-6">
                        Add to your journal.
                    </h2>
                    <form onSubmit={onUpload} data-testid="photo-upload-form" className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <label className="block">
                                <div className="text-[10px] uppercase tracking-[0.28em] text-[var(--fg-dim)] font-body mb-2">Photo file (JPG/PNG/HEIC · GPS in EXIF gets mapped)</div>
                                <input
                                    ref={fileRef}
                                    type="file"
                                    accept="image/*"
                                    data-testid="photo-upload-input"
                                    className="w-full bg-[var(--card-bg)] border border-[var(--border-hair)] text-[var(--fg)] px-4 py-3 font-body text-sm file:mr-4 file:bg-[#E87722] file:text-black file:border-0 file:px-4 file:py-2 file:font-display file:font-bold file:uppercase file:tracking-[0.15em] file:text-xs"
                                />
                            </label>
                            <label className="block">
                                <div className="text-[10px] uppercase tracking-[0.28em] text-[var(--fg-dim)] font-body mb-2">Caption (optional)</div>
                                <input
                                    type="text"
                                    value={caption}
                                    onChange={(e) => setCaption(e.target.value)}
                                    data-testid="photo-caption-input"
                                    placeholder="Slea Head at sunset..."
                                    className="w-full bg-[var(--card-bg)] border border-[var(--border-hair)] text-[var(--fg)] px-4 py-3 font-body text-sm focus:border-[#E87722] focus:outline-none"
                                />
                            </label>
                            <div className="md:col-span-2 flex items-center gap-2">
                                <div className="text-[10px] uppercase tracking-[0.28em] text-[var(--fg-dim)] font-body mr-2">Visibility</div>
                                {["private", "public"].map((v) => (
                                    <button
                                        type="button"
                                        key={v}
                                        onClick={() => setVisibility(v)}
                                        data-testid={`photo-vis-${v}`}
                                        className={`inline-flex items-center gap-2 px-4 py-2 font-display font-bold uppercase tracking-[0.18em] text-xs border transition-all ${
                                            visibility === v
                                                ? "bg-[#E87722] text-black border-[#E87722]"
                                                : "text-[var(--fg-muted)] border-[var(--border-hair)] hover:border-[#E87722] hover:text-[#E87722]"
                                        }`}
                                    >
                                        {v === "private" ? <Lock size={12} /> : <Globe size={12} />}
                                        {v}
                                    </button>
                                ))}
                                <div className="text-xs text-[var(--fg-dim)] font-body ml-3">
                                    {visibility === "public"
                                        ? "Appears on the public Journal for other riders."
                                        : "Only visible to you."}
                                </div>
                            </div>
                        </div>
                        <button
                            type="submit"
                            disabled={uploading}
                            data-testid="photo-upload-submit"
                            className="inline-flex items-center justify-center gap-3 bg-[#E87722] text-black px-8 py-4 font-display font-bold uppercase tracking-[0.2em] text-sm hover:bg-[#F28C38] transition-colors disabled:opacity-60 self-end"
                        >
                            <Upload size={16} strokeWidth={2.5} /> {uploading ? "Uploading…" : "Upload"}
                        </button>
                    </form>
                </div>
            </section>

            {/* Photo grid */}
            <section className="py-12 lg:py-16">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <div className="eyebrow mb-3">Your Photos</div>
                    <h2 className="font-display font-black uppercase text-[var(--fg)] text-2xl md:text-3xl leading-none tracking-tight mb-8">
                        {photos.length === 0 ? "Start your journal." : `${photos.length} photo${photos.length === 1 ? "" : "s"} in your journal.`}
                    </h2>
                    {photos.length === 0 ? (
                        <div className="text-[var(--fg-muted)] font-body text-sm py-6 border border-dashed border-[var(--border-hair)] p-6 text-center">
                            Upload your first photo above. Geotagged photos (GPS in EXIF) will land on your personal map automatically.
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                            {photos.map((p) => (
                                <div key={p.photo_id} data-testid={`user-photo-${p.photo_id}`} className="border border-[var(--border-hair)] bg-[var(--card-bg)]">
                                    <div className="aspect-square bg-black">
                                        <img
                                            src={userPhotoImageUrl(p.photo_id)}
                                            alt=""
                                            className="w-full h-full object-cover"
                                            loading="lazy"
                                        />
                                    </div>
                                    <div className="p-4">
                                        <div className="flex items-center justify-between mb-2">
                                            <div className="text-[10px] uppercase tracking-[0.24em] text-[var(--fg-dim)] font-body">
                                                {p.route_slug || "Unmatched"}
                                            </div>
                                            <div className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.16em] font-body ${p.visibility === "public" ? "text-[#E87722]" : "text-[var(--fg-dim)]"}`}>
                                                {p.visibility === "public" ? <Globe size={10} /> : <Lock size={10} />} {p.visibility}
                                            </div>
                                        </div>
                                        {p.caption && <div className="text-xs text-[var(--fg-muted)] font-body line-clamp-2 mb-3">{p.caption}</div>}
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => onToggleVisibility(p)}
                                                data-testid={`photo-toggle-vis-${p.photo_id}`}
                                                className="flex-1 text-[10px] uppercase tracking-[0.16em] py-2 border border-[var(--border-hair)] hover:border-[#E87722] hover:text-[#E87722] text-[var(--fg-muted)] font-display font-bold transition-colors"
                                            >
                                                Make {p.visibility === "public" ? "private" : "public"}
                                            </button>
                                            <button
                                                onClick={() => onDelete(p)}
                                                data-testid={`photo-delete-${p.photo_id}`}
                                                className="px-3 py-2 border border-[var(--border-hair)] hover:border-red-500 hover:text-red-500 text-[var(--fg-muted)] transition-colors"
                                                aria-label="Delete photo"
                                            >
                                                <Trash2 size={12} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
}


const SharePassportButton = ({ userId }) => {
    const [copied, setCopied] = useState(false);
    if (!userId) return null;
    const url = `${API}/passport/${userId}/share`;
    const onShare = async () => {
        const shareData = {
            title: "My Rider Passport",
            text: "Track my ride on MotorcycleRoutesIreland.com",
            url,
        };
        if (navigator.share) {
            try {
                await navigator.share(shareData);
                return;
            } catch (e) {
                if (e && e.name === "AbortError") return;
            }
        }
        try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            toast.success("Passport link copied", {
                description: "Paste it in WhatsApp / Instagram DMs — the card renders automatically.",
            });
            setTimeout(() => setCopied(false), 2400);
        } catch {
            toast.error("Couldn't copy — here's the URL", { description: url });
        }
    };
    return (
        <button
            type="button"
            onClick={onShare}
            data-testid="share-passport-btn"
            className="inline-flex items-center gap-2 bg-[#E87722] hover:bg-[#F28C38] text-black px-4 py-2 font-display font-bold uppercase tracking-[0.2em] text-xs transition-colors"
        >
            {copied ? <Check size={14} strokeWidth={2.5} /> : <Share2 size={14} strokeWidth={2.5} />}
            {copied ? "Link Copied" : "Share Passport"}
        </button>
    );
};
